/**
 * Application Configuration
 *
 * Central configuration for feature flags, service visibility, and app settings.
 * This file controls what's visible on public pages during phased rollout.
 *
 * @module lib/config
 */

import {
  Link2,
  QrCode,
  FileText,
  ClipboardList,
  Mail,
  Mailbox,
  MessageSquare,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// Service Configuration
// =============================================================================

/**
 * Service identifiers for all platform offerings.
 * Order matters - services appear in this order across the site.
 */
export type ServiceId =
  | 'links'
  | 'qr_codes'
  | 'pages'
  | 'forms'
  | 'email_newsletter'
  | 'direct_mail'
  | 'sms'
  | 'workflows';

/**
 * Complete service definition with all metadata needed for UI rendering.
 */
export interface ServiceDefinition {
  id: ServiceId;
  name: string;
  shortName: string; // For use in tight spaces (e.g., "QR Codes" → "QR")
  description: string;
  longDescription: string; // For features page
  icon: LucideIcon;
  gradient: string; // Tailwind gradient classes
  features: string[]; // Feature list for features page
  ctaText: string;
  ctaLink: string;
  /** Whether this service is currently available (false = "Coming Soon") */
  available: boolean;
  /** Minimum tier required for this service */
  minTier: 'free' | 'plus' | 'pro';
}

/**
 * Master service definitions - single source of truth for all service metadata.
 * This defines the complete catalog of services, regardless of visibility.
 */
export const SERVICES: Record<ServiceId, ServiceDefinition> = {
  links: {
    id: 'links',
    name: 'Short Links',
    shortName: 'Links',
    description:
      'Turn long URLs into clean, trackable links that tell you who clicked and where they came from.',
    longDescription:
      "Transform long URLs into clean, memorable links. Every click is tracked so you can see exactly who's engaging with your content.",
    icon: Link2,
    gradient: 'from-amber-500 to-orange-500',
    features: [
      'Instant link shortening — no account required',
      'Custom vanity URLs (e.g., arml.ink/sunday-service)',
      'Click analytics: location, device, time, referrer',
      'Organize with folders and tags',
      'Share analytics with your team',
    ],
    ctaText: 'Start shortening',
    ctaLink: '/',
    available: true,
    minTier: 'free',
  },
  qr_codes: {
    id: 'qr_codes',
    name: 'QR Codes',
    shortName: 'QR',
    description:
      'Generate branded QR codes for menus, flyers, and signage — all with built-in tracking.',
    longDescription:
      'Generate scannable codes for any link. Perfect for printed materials, signage, and in-person events where you want to bridge physical and digital.',
    icon: QrCode,
    gradient: 'from-blue-500 to-cyan-500',
    features: [
      'Instant QR code generation',
      'Download as PNG or SVG',
      'Track every scan with analytics',
      'Custom branding (Plus tier)',
      'Works offline — scans redirect through our CDN',
    ],
    ctaText: 'Generate QR codes',
    ctaLink: '/qr-codes',
    available: true,
    minTier: 'free',
  },
  pages: {
    id: 'pages',
    name: 'Landing Pages',
    shortName: 'Pages',
    description: 'Build beautiful one-page sites for events, announcements, or your link-in-bio.',
    longDescription:
      'Build simple, beautiful one-page sites without any coding. Perfect for events, announcements, or directing traffic to multiple destinations.',
    icon: FileText,
    gradient: 'from-purple-500 to-violet-500',
    features: [
      'Template-based page builder',
      'Mobile-responsive by default',
      'Hero sections with CTAs',
      'Built-in view analytics',
      'Connect to your short links',
    ],
    ctaText: 'Create a page',
    ctaLink: '/pages',
    available: true,
    minTier: 'plus',
  },
  forms: {
    id: 'forms',
    name: 'Forms',
    shortName: 'Forms',
    description: 'Collect RSVPs, coordinate volunteers, and manage signups with time slots.',
    longDescription:
      'Collect responses, coordinate volunteers, and manage event registrations. Like SignUpGenius, but integrated with your links.',
    icon: ClipboardList,
    gradient: 'from-emerald-500 to-green-500',
    features: [
      'Contact forms with custom fields',
      'Event signup with time slots',
      'Capacity limits and waitlists',
      'Email notifications on submission',
      'Export responses to CSV',
    ],
    ctaText: 'Create a form',
    ctaLink: '/forms',
    available: true,
    minTier: 'free',
  },
  email_newsletter: {
    id: 'email_newsletter',
    name: 'Email Newsletter',
    shortName: 'Email',
    description: 'Send campaigns to subscribers you build from links, forms, and events.',
    longDescription:
      'Send beautiful email campaigns to your subscribers. Build your list from link clicks, form submissions, and event signups.',
    icon: Mail,
    gradient: 'from-rose-500 to-pink-600',
    features: [
      'Drag-and-drop email builder',
      'Subscriber management and segmentation',
      'A/B testing for subject lines',
      'Open and click tracking',
      'Automated welcome sequences',
      'Unsubscribe management and compliance',
    ],
    ctaText: 'Coming Soon',
    ctaLink: '#',
    available: false,
    minTier: 'plus',
  },
  direct_mail: {
    id: 'direct_mail',
    name: 'Direct Mail',
    shortName: 'Mail',
    description: 'Send postcards and letters for event invitations and fundraising.',
    longDescription:
      'Send physical postcards, letters, and catalogs to your audience. Perfect for event invitations, thank you notes, and fundraising appeals.',
    icon: Mailbox,
    gradient: 'from-sky-500 to-blue-600',
    features: [
      'Postcards, letters, and self-mailers',
      'Address verification and standardization',
      'Track delivery status in real-time',
      'Personalization with merge fields',
      'Bulk pricing for large campaigns',
      'Coordinate with digital campaigns',
    ],
    ctaText: 'Coming Soon',
    ctaLink: '#',
    available: false,
    minTier: 'pro',
  },
  sms: {
    id: 'sms',
    name: 'SMS Campaigns',
    shortName: 'SMS',
    description: 'Send text message campaigns for event reminders and urgent announcements.',
    longDescription:
      'Reach your audience instantly with SMS campaigns. Perfect for event reminders, urgent announcements, and time-sensitive communications.',
    icon: MessageSquare,
    gradient: 'from-teal-500 to-cyan-600',
    features: [
      'Bulk SMS campaigns',
      'Two-way messaging support',
      'Scheduled message delivery',
      'Contact list management',
      'Delivery receipts and analytics',
      'Compliance with carrier regulations',
    ],
    ctaText: 'Coming Soon',
    ctaLink: '#',
    available: false,
    minTier: 'pro',
  },
  workflows: {
    id: 'workflows',
    name: 'Workflow Automation',
    shortName: 'Workflows',
    description: 'Automate repetitive tasks and connect your tools together.',
    longDescription:
      'Build automated workflows that trigger actions across your tools. Connect forms to emails, links to SMS, and create powerful multi-step automations.',
    icon: Workflow,
    gradient: 'from-indigo-500 to-purple-600',
    features: [
      'Visual workflow builder',
      'Trigger on form submissions, link clicks, or schedules',
      'Connect to email, SMS, and external services',
      'Conditional logic and branching',
      'Run history and debugging',
      'Pre-built templates for common automations',
    ],
    ctaText: 'Coming Soon',
    ctaLink: '#',
    available: false,
    minTier: 'pro',
  },
};

// =============================================================================
// Service Visibility Configuration
// =============================================================================

/**
 * Controls which services are visible on public pages.
 * Toggle these to incrementally reveal services as you launch them.
 *
 * When a service is enabled:
 * - It appears in the Landing page "Platform Tools" section
 * - It appears on the Features page
 * - It's mentioned in the Pricing page feature comparisons
 * - It's included in the About page platform overview
 * - The tool count updates automatically (e.g., "four powerful tools")
 *
 * Services can be enabled but marked as "Coming Soon" (available: false in SERVICES).
 * This shows the service but indicates it's not yet functional.
 *
 * @example
 * // Launch with just Links and QR Codes
 * ENABLED_SERVICES = { links: true, qr_codes: true, pages: false, ... }
 *
 * // Later, enable Pages
 * ENABLED_SERVICES = { links: true, qr_codes: true, pages: true, ... }
 */
export const ENABLED_SERVICES: Record<ServiceId, boolean> = {
  links: true,
  qr_codes: true,
  pages: true,
  forms: true,
  email_newsletter: false,
  direct_mail: false,
  sms: false,
  workflows: false,
};

// =============================================================================
// Stealth Mode
// =============================================================================

/**
 * Stealth Mode - When true, hides public pages entirely.
 * This is a higher-level toggle that overrides service visibility.
 *
 * What's hidden in stealth mode:
 * - Features page (redirects to home)
 * - Pricing page (redirects to home)
 * - About page (redirects to home)
 * - Platform Tools section on landing page
 * - Intelligence Flow section on landing page
 * - Community Mission section on landing page
 * - Footer product links
 *
 * What's visible:
 * - Demo section with link shortener
 * - Access request form
 * - Login for existing users
 *
 * When STEALTH_MODE is false, service visibility is controlled by ENABLED_SERVICES.
 */
export const STEALTH_MODE = false;

/**
 * Maintenance Mode Configuration
 *
 * When enabled, returns HTTP 503 Service Unavailable with proper headers.
 * This is the highest-level toggle that overrides ALL other configuration.
 *
 * Best Practices (see vision/maintenance.md):
 * - Returns 503 status (not 200/404) so search engines know it's temporary
 * - Includes Retry-After header for crawlers and clients
 * - Supports bypass mechanism via secret cookie for developers
 * - Minimal branding during beta pause period
 */
export const MAINTENANCE_MODE = {
  /** Enable maintenance mode */
  enabled: true,

  /** Expected duration in seconds (used for Retry-After header) */
  retryAfterSeconds: 86400, // 1 day (reasonable retry interval for long maintenance)

  /** Secret cookie name for developer bypass */
  bypassCookieName: '_maintenance_bypass',

  /** Secret value for bypass cookie (change this to your own secret) */
  bypassSecret: 'arml-maint-feb2026-k7x9p',

  /** Optional: Estimated return time (ISO 8601 format) */
  estimatedReturn: '2026-02-10T00:00:00Z',

  /** Optional: Reason for maintenance */
  reason:
    "We're taking a brief pause after our beta test to improve the platform based on your feedback.",
} as const;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get all enabled services as an array, in display order.
 */
export function getEnabledServices(): ServiceDefinition[] {
  const serviceOrder: ServiceId[] = [
    'links',
    'qr_codes',
    'pages',
    'forms',
    'email_newsletter',
    'direct_mail',
    'sms',
    'workflows',
  ];
  return serviceOrder.filter((id) => ENABLED_SERVICES[id]).map((id) => SERVICES[id]);
}

/**
 * Get count of enabled services.
 */
export function getEnabledServiceCount(): number {
  return getEnabledServices().length;
}

/**
 * Check if a specific service is enabled for public display.
 */
export function isServiceEnabled(serviceId: ServiceId): boolean {
  return ENABLED_SERVICES[serviceId] ?? false;
}

/**
 * Get the service count as a word (e.g., "two", "four", "six").
 * Falls back to the number if > 10.
 */
export function getServiceCountWord(): string {
  const count = getEnabledServiceCount();
  const words = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
  ];
  return words[count] ?? count.toString();
}

/**
 * Get formatted "X powerful tools" text for headlines.
 */
export function getToolsHeadline(): string {
  const count = getEnabledServiceCount();
  const word = getServiceCountWord();
  return `${word} powerful tool${count === 1 ? '' : 's'}`;
}

/**
 * Get a list of enabled service names for inline text.
 * @example "Links, QR codes, and Landing Pages"
 * @example "Links and QR codes"
 */
export function getServiceNamesList(): string {
  const services = getEnabledServices();
  if (services.length === 0) return '';
  if (services.length === 1) return services[0]?.name ?? '';
  if (services.length === 2) return `${services[0]?.name ?? ''} and ${services[1]?.name ?? ''}`;

  const allButLast = services
    .slice(0, -1)
    .map((s) => s.name)
    .join(', ');
  const lastService = services[services.length - 1];
  return `${allButLast}, and ${lastService?.name ?? ''}`;
}

/**
 * Get enabled services that are available (not "Coming Soon").
 */
export function getAvailableServices(): ServiceDefinition[] {
  return getEnabledServices().filter((s) => s.available);
}

/**
 * Get enabled services that are marked as "Coming Soon".
 */
export function getComingSoonServices(): ServiceDefinition[] {
  return getEnabledServices().filter((s) => !s.available);
}

// =============================================================================
// Dashboard Navigation Configuration
// =============================================================================

/**
 * Service ID to dashboard route mapping.
 * Used to filter navigation items based on enabled services.
 */
export const SERVICE_ROUTES: Record<ServiceId, string> = {
  links: '/links',
  qr_codes: '/qr-codes',
  pages: '/pages',
  forms: '/forms',
  email_newsletter: '/email-newsletter',
  direct_mail: '/direct-mail',
  sms: '/sms',
  workflows: '/workflows',
};

/**
 * Check if a dashboard route should be visible based on service configuration.
 * Routes not in the service map are always visible (e.g., Dashboard, Settings, Analytics).
 */
export function isRouteEnabled(href: string): boolean {
  // Find if this route maps to a service
  const serviceEntry = Object.entries(SERVICE_ROUTES).find(([, route]) => href === route);

  // If no service maps to this route, it's always visible
  if (!serviceEntry) return true;

  // Check if the service is enabled
  const [serviceId] = serviceEntry;
  return ENABLED_SERVICES[serviceId as ServiceId] ?? false;
}

/**
 * Get dashboard asset card configuration for enabled services only.
 * Returns service definitions with dashboard-specific metadata.
 */
export function getDashboardServices(): ServiceDefinition[] {
  return getEnabledServices();
}
